import type {OrderPriceTable} from '../server/functions/resellers/resellers.types.js'
import type {OrderAddress} from '../server/functions/orders/orders.types.js'
import calcDistance from './calcDistance.js'

type ComparableAddress = Partial<Pick<OrderAddress, 'postalCode' | 'nr' | 'addition'>>

const normalizePostalCode = (postalCode?: string) => (postalCode || '').replace(/\s/g, '').toUpperCase()
const normalizeValue = (value?: string | number) => String(value ?? '').trim().toUpperCase()

// Only postal code, house number and addition count, so a pickup address with slightly different coordinates, street or city still matches
function isCustomerAddress(address: ComparableAddress | undefined, customerAddress: ComparableAddress | undefined): boolean {
    if (!address || !customerAddress || !normalizePostalCode(customerAddress.postalCode)) {
        return false
    }

    return normalizePostalCode(address.postalCode) === normalizePostalCode(customerAddress.postalCode) &&
        normalizeValue(address.nr) === normalizeValue(customerAddress.nr) &&
        normalizeValue(address.addition) === normalizeValue(customerAddress.addition)
}

type ZoneDetermination = NonNullable<OrderPriceTable['zoneDetermination']>

// Price tables saved before zoneDetermination existed only have useDeliveryAreas: on means delivery areas, off straight line
function getZoneDetermination(priceTable: Pick<OrderPriceTable, 'zoneDetermination' | 'useDeliveryAreas'>): ZoneDetermination {
    return priceTable.zoneDetermination || (priceTable.useDeliveryAreas ? 'deliveryAreas' : 'straightLine')
}

type DistanceZoneOptions = {
    distanceZones: OrderPriceTable['distanceZones']
    zoneDetermination: ZoneDetermination
    addresses: Pick<OrderAddress, 'type' | 'position' | 'postalCode' | 'nr' | 'addition'>[]
    // Delivery zone ids per order address, by the same index as addresses
    addressesDeliveryZoneIds: string[][]
    customerAddress?: ComparableAddress & {position?: {lat?: number, lng?: number}}
    // Road distance in meters from the customer address per order address, by the same index as addresses. Only for
    // carDistance and bikeDistance; an address without a road distance falls back to the straight line distance.
    roadDistances?: (number | undefined)[]
}

// A pickup at the customer's own address always falls in the first zone (distance 0 or the customer's delivery area), so it
// doesn't count, unless no other address is left
function getZoneAddressIndexes(addresses: DistanceZoneOptions['addresses'], customerAddress: DistanceZoneOptions['customerAddress']): number[] {
    const addressIndexes = addresses.map((address, index) => index)
        .filter((index) => !(addresses[index].type === 'pickup' && isCustomerAddress(addresses[index], customerAddress)))

    return addressIndexes.length ? addressIndexes : addresses.map((address, index) => index)
}

type AddressDistanceOptions = Pick<DistanceZoneOptions, 'zoneDetermination' | 'addresses' | 'customerAddress' | 'roadDistances'>

function getAddressDistance({zoneDetermination, addresses, customerAddress, roadDistances}: AddressDistanceOptions, index: number): number {
    const roadDistance = ['carDistance', 'bikeDistance'].includes(zoneDetermination) ? roadDistances?.[index] : undefined

    return typeof roadDistance === 'number' ? roadDistance : calcDistance(customerAddress?.position, addresses[index]?.position)
}

// Returns the index of the first zone that contains an order address
function getDistanceZoneIndex(options: DistanceZoneOptions): number {
    const {distanceZones, zoneDetermination, addresses, addressesDeliveryZoneIds, customerAddress} = options
    const addressIndexes = getZoneAddressIndexes(addresses, customerAddress)

    return (distanceZones || []).findIndex((zone) => {
        if (zoneDetermination === 'deliveryAreas') {
            return addressIndexes.some((index) => {
                const deliveryZoneIds = addressesDeliveryZoneIds[index] || []

                return deliveryZoneIds.some((deliveryZoneId) => zone.deliveryZones?.includes(deliveryZoneId))
            })
        }

        return addressIndexes.some((index) => {
            if (!zone.maxDistance) return true

            return getAddressDistance(options, index) <= zone.maxDistance
        })
    })
}

// The distance in meters that decides the zone, that of the nearest address that counts. A road distance that is missing
// for one of these addresses is replaced by the straight line distance, roadDistanceUnavailable shows that happened.
function getZoneDistance(options: AddressDistanceOptions): {distance: number, roadDistanceUnavailable: boolean} | undefined {
    const {zoneDetermination, addresses, customerAddress, roadDistances} = options

    if (zoneDetermination === 'deliveryAreas' || !addresses.length) {
        return undefined
    }

    const addressIndexes = getZoneAddressIndexes(addresses, customerAddress)
    const distance = Math.min(...addressIndexes.map((index) => getAddressDistance(options, index)))
    const roadDistanceUnavailable = ['carDistance', 'bikeDistance'].includes(zoneDetermination) && addressIndexes.some((index) => typeof roadDistances?.[index] !== 'number')

    return {distance, roadDistanceUnavailable}
}

export type {ZoneDetermination}

export {
    isCustomerAddress,
    getZoneDetermination,
    getDistanceZoneIndex,
    getZoneDistance
}
